/**
 * listenFormat.ts — Schrift und Größe von Listenpunkten.
 *
 * Die Nummer bzw. der Punkt einer Liste (`::marker`) richtet sich nach der
 * Schrift des `<li>` selbst, NICHT nach dem Text darin. `execCommand` legt
 * Schrift und Größe aber immer nur als `<font>` um den Text. Ergebnis vorher:
 * Text in 36 pt, davor eine winzige „1." in Times 11. Deshalb bekommt ein
 * Listenpunkt, dessen Text vollständig umformatiert wird, die Angabe zusätzlich
 * als Inline-Style am `<li>`. Der Server-Sanitizer lässt `style` an allen
 * Elementen durch (`mail-html-sanitizer.ts`, `'*': ['style', …]`), die Angabe
 * erreicht also auch den Empfänger.
 *
 * Reine DOM-Helfer ohne Abhängigkeit zu `formatUebertragen.ts`.
 */

/** Text des Listenpunkts selbst — ohne verschachtelte Unterlisten, ohne Leerraum-Knoten. */
import { aktiveAuswahl } from "../../windowManager/browserFenster/fensterRegister";
export function eigeneTextknoten(li: HTMLElement): Text[] {
    const out: Text[] = [];
    const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (!(n.textContent ?? "").replace(/[​﻿]/g, "").trim()) continue;
        // In einer Unterliste? Dann gehört der Text einem anderen Punkt.
        let p = n.parentElement;
        let fremd = false;
        while (p && p !== li) {
            if (p.tagName === "UL" || p.tagName === "OL") { fremd = true; break; }
            p = p.parentElement;
        }
        if (!fremd) out.push(n as Text);
    }
    return out;
}

/** Liegt der ganze Textknoten in `range`? */
function ganzImBereich(range: Range, t: Text): boolean {
    try {
        return range.comparePoint(t, 0) === 0 && range.comparePoint(t, t.length) === 0;
    } catch {
        return false;
    }
}

/**
 * Die Listenpunkte, deren Schrift eine Formatierung der aktuellen Auswahl
 * mitbekommen soll:
 * - Punkte, deren eigener Text VOLLSTÄNDIG markiert ist (wer nur ein Wort
 *   vergrößert, will keine riesige Nummer davor),
 * - ein leerer Punkt, in dem der Cursor steht (neuer Punkt, noch ohne Text).
 */
export function listenpunkteDerAuswahl(root: HTMLElement | null): HTMLElement[] {
    if (!root) return [];
    const sel = typeof window !== "undefined" ? aktiveAuswahl() : null;
    if (!sel || sel.rangeCount === 0) return [];
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return [];
    const treffer: HTMLElement[] = [];
    root.querySelectorAll<HTMLElement>("li").forEach((li) => {
        if (!range.intersectsNode(li)) return;
        const texte = eigeneTextknoten(li);
        if (texte.length === 0) {
            treffer.push(li);
            return;
        }
        if (!range.collapsed && texte.every((t) => ganzImBereich(range, t))) treffer.push(li);
    });
    return treffer;
}

export type ListenpunktFormat = {
    /** Font-Stack oder Schriftname; `null` entfernt die Angabe am `<li>`. */
    fontFamily?: string | null;
    /** Größe in pt; `null` entfernt die Angabe am `<li>`. */
    sizePt?: number | null;
};

/** Setzt (oder entfernt) Schrift/Größe als Inline-Style an den Listenpunkten. */
export function setzeListenpunktFormat(lis: readonly HTMLElement[], f: ListenpunktFormat): void {
    for (const li of lis) {
        if (f.fontFamily !== undefined) {
            if (f.fontFamily) li.style.fontFamily = f.fontFamily;
            else li.style.removeProperty("font-family");
        }
        if (f.sizePt !== undefined) {
            if (f.sizePt) li.style.fontSize = `${f.sizePt}pt`;
            else li.style.removeProperty("font-size");
        }
        if (li.getAttribute("style")?.trim() === "") li.removeAttribute("style");
    }
}

/**
 * Der Knoten, dessen Format „am Cursor" gilt.
 *
 * Steht der Cursor an einer Elementgrenze (Container = `<li>`, Offset = Index),
 * hilft der Container selbst nicht weiter: An ihm hängt keine Schrift, die steckt
 * im `<font>` darin. Gemeint ist — wie in Word — das Zeichen VOR dem Cursor;
 * am Anfang das erste danach.
 */
export function knotenAmCursor(container: Node, offset: number): Node {
    if (container.nodeType === Node.TEXT_NODE) return container;
    const kinder = container.childNodes;
    if (kinder.length === 0) return container;
    const davor = offset > 0;
    let n: Node = kinder[Math.min(davor ? offset - 1 : offset, kinder.length - 1)];
    while (n.firstChild) n = davor ? n.lastChild! : n.firstChild;
    return n;
}
